import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import ApprovalHistory from '../../components/checklists/ApprovalHistory';

const ChecklistReview = () => {
  console.log('🎯 ChecklistReview component starting...');

  const { user } = useAuth();
  const baseURL = import.meta.env.VITE_API_BASE_URL || '';
  const token = localStorage.getItem('token');
  const [checklistItems, setChecklistItems] = useState([]);
  const [gymOnboardedGyms, setGymOnboardedGyms] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOnboardGym, setSelectedOnboardGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('review'); // 'review' or 'onboard'
  const [filter, setFilter] = useState({
    status: 'all',
    gymName: '',
    category: 'all'
  });
  const [approvalComment, setApprovalComment] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showOnboardModal, setShowOnboardModal] = useState(false);

  console.log('🔍 ChecklistReview rendering:', { user: user?.email, role: user?.role?.name, level: user?.role?.level });

  // Check if user is properly loaded
  if (!user) {
    console.log('⚠️ No user found in ChecklistReview');
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">Loading User Data...</h2>
          <p className="text-yellow-700">Please wait while we load your profile.</p>
        </div>
      </div>
    );
  }

  // Allow Head of Acquisition (3), COO (2), and CEO (1) to review/approve
  const canReview = user?.role?.level === 3 || user?.role?.level === 2 || user?.role?.level === 1;
  const isCOO = user?.role?.level === 2;
  const isHeadOfAcquisition = user?.role?.level === 3;
  const isCEO = user?.role?.level === 1;

  if (!canReview) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">Access Restricted</h2>
          <p className="text-yellow-700">You do not have permission to review/approve checklist items.</p>
        </div>
      </div>
    );
  }

  // Fetch checklist items
  const fetchChecklistItems = async () => {
    try {
      console.log('📡 Fetching checklist items for review...');
      setError(null);

      // Use the review endpoint that filters based on role
      const response = await fetch(`${baseURL}/api/checklists/review`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('📡 Review items response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        let items = data.items || data || [];

        // Additional filtering for COO
        if (isCOO) {
          items = items.filter(item => {
            // COO should only see items that:
            // 1. Have Head of Acquisition approval
            // 2. Don't have COO approval yet
            const hasHoAApproval = item.approvals?.some(approval =>
              approval.approverRole === 'Head of Acquisition' && approval.status === 'approved'
            );
            const hasCOOApproval = item.approvals?.some(approval =>
              approval.approverRole === 'COO'
            );

            return hasHoAApproval && !hasCOOApproval;
          });
        }

        setChecklistItems(items);
      } else if (response.status === 404) {
        // Fallback: If review endpoint not found, use the regular items endpoint
        console.log('⚠️ Review endpoint not found, falling back to regular items endpoint');
        const fallbackResponse = await fetch(`${baseURL}/api/checklists/items`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('📊 Fallback items data:', fallbackData);

          // Apply filtering logic on frontend
          let itemsWithProof = (fallbackData.items || fallbackData || []).filter(item => {
            const hasMediaUploads = item.media && item.media.length > 0;
            const isSubmittedByFacility = item.status === 'submitted';
            return hasMediaUploads && isSubmittedByFacility;
          });

          // Additional filtering for COO
          if (isCOO) {
            itemsWithProof = itemsWithProof.filter(item => {
              const hasHoAApproval = item.approvals?.some(approval =>
                approval.approverRole === 'Head of Acquisition' && approval.status === 'approved'
              );
              const hasCOOApproval = item.approvals?.some(approval =>
                approval.approverRole === 'COO'
              );

              return hasHoAApproval && !hasCOOApproval;
            });
          }

          setChecklistItems(itemsWithProof);
        } else {
          const errorText = await fallbackResponse.text();
          console.error('❌ Failed to fetch fallback items:', fallbackResponse.status, errorText);
          setError(`Failed to load items: ${fallbackResponse.status} ${fallbackResponse.statusText}`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch review items:', response.status, errorText);
        setError(`Failed to load review items: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error fetching review items:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Gym Onboarded gyms with their checklists
  const fetchGymOnboardedGyms = async () => {
    try {
      console.log('📡 Fetching Onboarded gyms...');
      setOnboardLoading(true);

      const response = await fetch(`${baseURL}/api/gyms?stage=Gym Onboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const gyms = data.gyms || [];
        console.log('📊 Onboarded gyms:', gyms.length);
        setGymOnboardedGyms(gyms);
      } else {
        console.error('❌ Failed to fetch Onboarded gyms:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching Onboarded gyms:', error);
    } finally {
      setOnboardLoading(false);
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    console.log('🚀 ChecklistReview useEffect triggered');
    fetchChecklistItems();
  }, []);

  // Fetch Onboarded gyms when tab changes to 'onboard'
  useEffect(() => {
    if (activeTab === 'onboard' && gymOnboardedGyms.length === 0) {
      fetchGymOnboardedGyms();
    }
  }, [activeTab]);

  // Validation for COO approval
  const validateForCOOApproval = (item) => {
    if (!isCOO) return { canApprove: true, errors: [] };

    const errors = [];

    // Check if Head of Acquisition has approved
    const hasHoAApproval = item.approvals?.some(approval =>
      approval.approverRole === 'Head of Acquisition' && approval.status === 'approved'
    );
    if (!hasHoAApproval) {
      errors.push('Head of Acquisition approval required');
    }

    // Check required photos
    if (item.requiresPreImage) {
      const preImages = item.media?.filter(m => m.type === 'pre_image') || [];
      if (preImages.length === 0) {
        errors.push('Pre-work image required');
      }
    }

    if (item.requiresPostImage) {
      const postImages = item.media?.filter(m => m.type === 'post_image') || [];
      if (postImages.length === 0) {
        errors.push('Post-work image required');
      }
    }

    // Check GPS if required
    if (item.requiresGPS) {
      const hasGPS = item.media?.some(m => m.gpsLatitude && m.gpsLongitude);
      if (!hasGPS) {
        errors.push('GPS coordinates required');
      }
    }

    // Check timestamp
    const hasTimestamp = item.media?.some(m => m.timestamp);
    if (!hasTimestamp && item.media?.length > 0) {
      errors.push('Timestamp required');
    }

    return { canApprove: errors.length === 0, errors };
  };

  // Add error boundary-like error handling
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Task Review</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchChecklistItems();
            }}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Handle approval/rejection
  const handleApproval = async (action, comments) => {
    if (!selectedItem) return;

    // For COO, validate requirements
    if (isCOO) {
      const validation = validateForCOOApproval(selectedItem);
      if (!validation.canApprove && action === 'approved') {
        toast.error(`Cannot approve: ${validation.errors.join(', ')}`);
        return;
      }
    }

    try {
      const response = await fetch(`${baseURL}/api/approvals/checklist/${selectedItem.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: action, // must be 'approved' or 'rejected'
          comments
        })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Checklist item updated successfully');
        await fetchChecklistItems();
        setApprovalComment('');
        setShowDetailModal(false);
        setSelectedItem(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update checklist item');
      }
    } catch (error) {
      toast.error('Network error: ' + error.message);
    }
  };

  // Filter items - simplified since backend handles main filtering
  const filteredItems = (checklistItems || []).filter(item => {
    // Apply frontend filters only
    if (filter.status !== 'all' && item?.status !== filter.status) return false;
    if (filter.gymName && !item?.gym?.name?.toLowerCase().includes(filter.gymName.toLowerCase())) return false;
    if (filter.category !== 'all' && item?.category !== filter.category) return false;
    return true;
  });

  // Get unique categories - add safety check
  const categories = [...new Set((checklistItems || []).map(item => item?.category).filter(Boolean))];

  // Tab counts (always from full list, not filtered)
  const allCount = checklistItems.length;
  const pendingCount = checklistItems.filter(item => item.status === 'pending').length;
  const inProgressCount = checklistItems.filter(item => item.status === 'in_progress').length;
  const submittedCount = checklistItems.filter(item => item.status === 'submitted').length;

  // Open detail modal
  const openDetailModal = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
    setApprovalComment('');
  };

  // Open go-live modal
  const openOnboardModal = (gym) => {
    setSelectedOnboardGym(gym);
    setShowOnboardModal(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Checklist Review Center</h1>
        <p className="text-gray-600">Review and validate checklist items with photo proof and GPS verification</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="text-2xl font-bold text-gray-900">{allCount}</div>
          <div className="text-sm text-gray-600">Total Items</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <div className="text-2xl font-bold text-gray-900">{pendingCount + inProgressCount}</div>
          <div className="text-sm text-gray-600">Pending</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="text-2xl font-bold text-gray-900">{submittedCount}</div>
          <div className="text-sm text-gray-600">In Review</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="text-2xl font-bold text-gray-900">{gymOnboardedGyms.length}</div>
          <div className="text-sm text-gray-600">Gym Onboarded</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          <button
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'review'
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('review')}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Review Checklists
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">{allCount}</span>
            </div>
          </button>
          <button
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'onboard'
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('onboard')}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Gym Onboarded
              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold">{gymOnboardedGyms.length}</span>
            </div>
          </button>
        </div>
      </div>

      {/* Review Checklists Tab Content */}
      {activeTab === 'review' && (
        <>
          {/* Filters Bar */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Status Filter Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {[
                    { key: 'all', label: 'All', count: allCount },
                    { key: 'pending', label: 'Pending', count: pendingCount },
                    { key: 'in_progress', label: 'In Progress', count: inProgressCount },
                    { key: 'submitted', label: 'In Review', count: submittedCount }
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                        filter.status === key
                          ? 'bg-green-500 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setFilter(prev => ({ ...prev, status: key }))}
                    >
                      {label} ({count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Input */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by gym name..."
                    value={filter.gymName}
                    onChange={(e) => setFilter(prev => ({ ...prev, gymName: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <select
                value={filter.category}
                onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Checklist Items Grid */}
          {filteredItems.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-gray-300 text-6xl mb-4">
                <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No checklist items found</h3>
              <p className="text-gray-500">There are no items matching your current filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
                  onClick={() => openDetailModal(item)}
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{item.title}</h3>
                      <span className={`flex-shrink-0 ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                        item.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        item.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {item.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{item.gym?.name}</p>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {item.category}
                      </div>
                      {item.isMandatory && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
                          MANDATORY
                        </span>
                      )}
                    </div>

                    {/* Media Preview */}
                    <div className="flex items-center gap-2 mb-3">
                      {item.media && item.media.length > 0 ? (
                        <>
                          <div className="flex items-center gap-1 text-sm text-green-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {item.media.length} file(s) uploaded
                          </div>
                          {item.media.some(m => m.gpsLatitude && m.gpsLongitude) && (
                            <div className="flex items-center gap-1 text-sm text-blue-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                              GPS
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">No media uploaded</span>
                      )}
                    </div>

                    {/* Assigned User */}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {item.assignedUser?.name || 'Unassigned'}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="px-4 py-3 bg-gray-50 rounded-b-lg">
                    <button
                      className="w-full py-2 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetailModal(item);
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Review Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Gym Onboarded Tab Content */}
      {activeTab === 'onboard' && (
        <>
          {onboardLoading ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading Onboarded gyms...</p>
            </div>
          ) : gymOnboardedGyms.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-gray-300 text-6xl mb-4">
                <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Onboarded gyms</h3>
              <p className="text-gray-500">Gyms will appear here after completing the full acquisition pipeline.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gymOnboardedGyms.map(gym => (
                <div
                  key={gym.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
                  onClick={() => openOnboardModal(gym)}
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{gym.name}</h3>
                      <span className="flex-shrink-0 ml-2 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Gym Onboard
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{gym.location || gym.address}</p>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <div className="space-y-2 text-sm">
                      {gym.launch_date && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Launched: {new Date(gym.launch_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                      {gym.assignedUser && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {gym.assignedUser.name}
                        </div>
                      )}
                      {gym.checklistItems && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          {gym.checklistItems.filter(i => i.status === 'approved').length} / {gym.checklistItems.length} tasks completed
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="px-4 py-3 bg-gray-50 rounded-b-lg">
                    <button
                      className="w-full py-2 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOnboardModal(gym);
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Detail Modal for Checklist Items */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{selectedItem.title}</h3>
                <p className="text-green-100 text-sm">{selectedItem.gym?.name} - {selectedItem.category}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedItem(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* Item Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    selectedItem.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                    selectedItem.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                    selectedItem.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {selectedItem.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Assigned To</div>
                  <div className="font-medium text-gray-900">{selectedItem.assignedUser?.name || 'Unassigned'}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Category</div>
                  <div className="font-medium text-gray-900">{selectedItem.category}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Priority</div>
                  <div className="font-medium text-gray-900">
                    {selectedItem.isMandatory ? (
                      <span className="text-red-600">Mandatory</span>
                    ) : (
                      <span className="text-gray-600">Optional</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedItem.description && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{selectedItem.description}</p>
                </div>
              )}

              {/* Media Display - Side by Side Pre/Post Images */}
              {selectedItem.media && selectedItem.media.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Media Proof</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Pre-Work Images */}
                    <div>
                      <h5 className="font-medium text-gray-700 mb-3 flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Before Work
                      </h5>
                      <div className="space-y-3">
                        {selectedItem.media.filter(m => m.type === 'pre_image').length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 text-3xl mb-2">
                              <svg className="w-10 h-10 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="text-gray-500 text-sm">No pre-work images</p>
                          </div>
                        ) : (
                          selectedItem.media.filter(m => m.type === 'pre_image').map(mediaItem => (
                            <div key={mediaItem.id} className="border border-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={`${baseURL}/uploads/${mediaItem.filename?.startsWith('facility-') ? 'facility/' : ''}${mediaItem.filename || mediaItem.url || ''}`}
                                alt="Pre-work"
                                className="w-full h-48 object-cover bg-gray-100"
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxIDNIMy4wMDNDMi40NSAzIDIgMy40NSAyIDMuOTkzVjIwLjAwN0MyIDIwLjU1IDIuNDUgMjEgMy4wMDMgMjFIMjEuMDIzQzIxLjU1IDIxIDIyIDIwLjU1IDIyIDIwLjAwN0YzLjk5M0MyMiAzLjQ1IDIxLjU1IDMgMjEgM1pNMjAgMTlINFY1SDE5TDIwIDE5Wk0xNy4xIDExLjlMMTMuNzUgMTcuODVMMTIuMiAxNi4yNUMxMS44NSAxNS45IDExLjMgMTUuOSAxMC45NSAxNi4yNUMxMC42IDE2LjYgMTAuNiAxNy4xNSAxMC45NSAxNy41TDEzIDIxLjVMMTggMTNMMTcuMSAxMS45WiIgZmlsbD0iIzlEQTJBNiIvPgo8L3N2Zz4=';
                                }}
                              />
                              <div className="p-3 bg-gray-50 text-xs text-gray-600 space-y-1">
                                <div className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {mediaItem.timestamp ? new Date(mediaItem.timestamp).toLocaleString() : 'No timestamp'}
                                </div>
                                {mediaItem.gpsLatitude && mediaItem.gpsLongitude && (
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    {mediaItem.gpsLatitude.toFixed(6)}, {mediaItem.gpsLongitude.toFixed(6)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Post-Work Images */}
                    <div>
                      <h5 className="font-medium text-gray-700 mb-3 flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        After Work
                      </h5>
                      <div className="space-y-3">
                        {selectedItem.media.filter(m => m.type === 'post_image').length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 text-3xl mb-2">
                              <svg className="w-10 h-10 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="text-gray-500 text-sm">No post-work images</p>
                          </div>
                        ) : (
                          selectedItem.media.filter(m => m.type === 'post_image').map(mediaItem => (
                            <div key={mediaItem.id} className="border border-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={`${baseURL}/uploads/${mediaItem.filename?.startsWith('facility-') ? 'facility/' : ''}${mediaItem.filename || mediaItem.url || ''}`}
                                alt="Post-work"
                                className="w-full h-48 object-cover bg-gray-100"
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxIDNIMy4wMDNDMi40NSAzIDIgMy40NSAyIDMuOTkzVjIwLjAwN0MyIDIwLjU1IDIuNDUgMjEgMy4wMDMgMjFIMjEuMDIzQzIxLjU1IDIxIDIyIDIwLjU1IDIyIDIwLjAwN0YzLjk5M0MyMiAzLjQ1IDIxLjU1IDMgMjEgM1pNMjAgMTlINFY1SDE5TDIwIDE5Wk0xNy4xIDExLjlMMTMuNzUgMTcuODVMMTIuMiAxNi4yNUMxMS44NSAxNS45IDExLjMgMTUuOSAxMC45NSAxNi4yNUMxMC42IDE2LjYgMTAuNiAxNy4xNSAxMC45NSAxNy41TDEzIDIxLjVMMTggMTNMMTcuMSAxMS45WiIgZmlsbD0iIzlEQTJBNiIvPgo8L3N2Zz4=';
                                }}
                              />
                              <div className="p-3 bg-gray-50 text-xs text-gray-600 space-y-1">
                                <div className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {mediaItem.timestamp ? new Date(mediaItem.timestamp).toLocaleString() : 'No timestamp'}
                                </div>
                                {mediaItem.gpsLatitude && mediaItem.gpsLongitude && (
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    {mediaItem.gpsLatitude.toFixed(6)}, {mediaItem.gpsLongitude.toFixed(6)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Other Media */}
                  {selectedItem.media.filter(m => m.type !== 'pre_image' && m.type !== 'post_image').length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-medium text-gray-700 mb-3 text-sm">Other Files</h5>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedItem.media.filter(m => m.type !== 'pre_image' && m.type !== 'post_image').map(mediaItem => (
                          <a
                            key={mediaItem.id}
                            href={`${baseURL}/uploads/${mediaItem.filename?.startsWith('facility-') ? 'facility/' : ''}${mediaItem.filename || mediaItem.url || ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm text-blue-600 hover:underline truncate">
                              {mediaItem.originalName || mediaItem.filename || 'Download File'}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Approval History */}
              <ApprovalHistory
                approvalLogs={selectedItem.approvalLogs}
                approvals={selectedItem.approvals}
              />

              {/* Approval Actions */}
              {selectedItem.status === 'submitted' && (isHeadOfAcquisition || isCOO || isCEO) && (
                <div className="mt-6 border-t pt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {isHeadOfAcquisition ? 'Head of Acquisition Review' : isCOO ? 'COO Review' : 'CEO Final Review'}
                  </h4>

                  <textarea
                    value={approvalComment}
                    onChange={e => setApprovalComment(e.target.value)}
                    placeholder="Enter your review comment (required for rejection)..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-4 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows={3}
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        if (!approvalComment.trim()) {
                          toast.error('Please enter a comment for approval');
                          return;
                        }
                        handleApproval('approved', approvalComment);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        if (!approvalComment.trim()) {
                          toast.error('Please enter a reason for rejection');
                          return;
                        }
                        handleApproval('rejected', approvalComment);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboard Detail Modal */}
      {showOnboardModal && selectedOnboardGym && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{selectedOnboardGym.name}</h3>
                <p className="text-green-100 text-sm">{selectedOnboardGym.location || selectedOnboardGym.address}</p>
              </div>
              <button
                onClick={() => {
                  setShowOnboardModal(false);
                  setSelectedOnboardGym(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* Gym Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Contact Person</div>
                  <div className="font-medium text-gray-900">{selectedOnboardGym.contact_person || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-medium text-gray-900">{selectedOnboardGym.phone || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-medium text-gray-900 text-sm break-all">{selectedOnboardGym.email || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Assigned BD</div>
                  <div className="font-medium text-gray-900">{selectedOnboardGym.assignedUser?.name || 'N/A'}</div>
                </div>
              </div>

              {/* Handover Details */}
              {(selectedOnboardGym.salesHeadName || selectedOnboardGym.opsHeadName) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Handover Team
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-blue-600">Sales Head</div>
                      <div className="font-medium text-blue-900">{selectedOnboardGym.salesHeadName || 'Not Assigned'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-blue-600">Operations Head</div>
                      <div className="font-medium text-blue-900">{selectedOnboardGym.opsHeadName || 'Not Assigned'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stage History */}
              {selectedOnboardGym.stageHistory && selectedOnboardGym.stageHistory.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    Stage Progression
                  </h4>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></div>
                    <div className="space-y-3">
                      {selectedOnboardGym.stageHistory.map((history, index) => (
                        <div key={history.id} className="relative pl-10">
                          <div className="absolute left-2 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{history.fromStage || 'New'}</span>
                              <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{history.toStage}</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {history.changer?.name || 'Unknown'} - {formatDateTime(history.changedAt)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Checklist Summary */}
              {selectedOnboardGym.checklistItems && selectedOnboardGym.checklistItems.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Checklist Summary
                  </h4>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="bg-green-50 p-3 rounded text-center">
                      <div className="text-xl font-bold text-green-600">
                        {selectedOnboardGym.checklistItems.filter(i => i.status === 'approved').length}
                      </div>
                      <div className="text-xs text-green-700">Approved</div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded text-center">
                      <div className="text-xl font-bold text-blue-600">
                        {selectedOnboardGym.checklistItems.filter(i => i.status === 'submitted').length}
                      </div>
                      <div className="text-xs text-blue-700">In Review</div>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded text-center">
                      <div className="text-xl font-bold text-yellow-600">
                        {selectedOnboardGym.checklistItems.filter(i => i.status === 'pending' || i.status === 'in_progress').length}
                      </div>
                      <div className="text-xs text-yellow-700">Pending</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-xl font-bold text-gray-600">
                        {selectedOnboardGym.checklistItems.length}
                      </div>
                      <div className="text-xs text-gray-700">Total</div>
                    </div>
                  </div>

                  {/* Checklist Items */}
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                    {selectedOnboardGym.checklistItems.map((item, index) => (
                      <div
                        key={item.id}
                        className={`p-3 flex items-center justify-between ${index !== selectedOnboardGym.checklistItems.length - 1 ? 'border-b border-gray-100' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            item.status === 'approved' ? 'bg-green-100 text-green-600' :
                            item.status === 'submitted' ? 'bg-blue-100 text-blue-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {item.status === 'approved' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.title}</div>
                            <div className="text-xs text-gray-500">{item.category}</div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.status === 'approved' ? 'bg-green-100 text-green-800' :
                          item.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowOnboardModal(false);
                  setSelectedOnboardGym(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistReview;
